import { alphaSorter, getLocalName, isEqual } from 'vet-bones/utils';

import {
  ModelConceptClass,
  ModelConceptHierarchy,
  ModelConceptHierarchyLabel,
  ModelConceptRelationship,
  ModelConceptRelationshipLabel,
} from 'src/containers/ProjectDiagram/types/data';
import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';
import { Model, ModelData } from 'src/containers/ProjectDiagram/types/model';
import { setColorIdForIri } from 'src/containers/ProjectDiagram/utils/colorIds';
import { addDomainToAttributeModelData } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/addDomainToAttributeModelData';
import { addModelsToAttributeModelData } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/addModelsToAttributeModelData';
import { addRangeToAttributeModelData } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/addRangeToAttributeModelData';
import {
  createModelConceptClass,
  getModelConceptClassKey,
} from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { createModelConceptHierarchy } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/createModelConceptHierarchy';
import { createModelConceptHierarchyLabel } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/createModelConceptHierarchyLabel';
import { addUnconnectedModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addUnconnectedModelConceptRelationship';
import { createModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationship';
import { createModelConceptRelationshipLabel } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationshipLabel';
import { setPropertyType } from 'src/containers/ProjectDiagram/utils/propertyType/setPropertyType';
import { addRule } from 'src/containers/ProjectDiagram/utils/rules/addRule';
import { getRuleModel } from 'src/containers/ProjectDiagram/utils/rules/getRuleModel';
import { isRuleValid } from 'src/containers/ProjectDiagram/utils/rules/isRuleValid';
import { updateRule } from 'src/containers/ProjectDiagram/utils/rules/updateRule';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { ProjectModel } from 'src/store/state/projects';

const mergeLabelsByIri = (
  prevLabels: Record<string, string>,
  labels: Record<string, string>
) => {
  const nextLabels = { ...prevLabels };
  Object.entries(labels).forEach(([iri, label]) => {
    const existingLabel = nextLabels[iri];
    if (!existingLabel || existingLabel === getLocalName(iri)) {
      nextLabels[iri] = label;
    }
  });
  return nextLabels;
};

const createOrUpdateData = <
  Data extends
    | ModelConceptClass
    | ModelConceptHierarchy
    | ModelConceptRelationship
>(
  data: Data,
  dataArray: Data[],
  modelName: string,
  model: Model,
  onAddData: Model['addNodeData'] | Model['addLinkData']
) => {
  const existingData = dataArray.find((existing) => existing.key === data.key);
  if (!existingData) {
    onAddData.call(model, data);
    return;
  }

  if (!existingData.models.includes(modelName)) {
    setDataProperty<Data>(model)(
      existingData,
      'models',
      [...existingData.models, modelName].sort(alphaSorter)
    );
  }
};

const createLabelData = <
  Data extends ModelConceptHierarchyLabel | ModelConceptRelationshipLabel
>(
  data: Data,
  model: Model
) => {
  const existingData = model.nodeDataArray.find(
    (existing) => existing.key === data.key
  );
  if (!existingData) {
    model.addNodeData(data);
  }
};

const doesModelHaveClass = (model: Model, iri: string) => {
  const key = getModelConceptClassKey(iri);
  return Boolean(model.nodeDataArray.find((data) => data.key === key));
};

const addMetadataToDiagramModel = (
  projectModel: ProjectModel,
  model: Model
) => {
  if (projectModel.colorIdsByIri) {
    setDataProperty<ModelData>(model)(model.modelData, 'colorIdsByIri', {
      // only add new, don't overwrite
      ...projectModel.colorIdsByIri,
      ...model.modelData.colorIdsByIri,
    });
  }

  if (projectModel.descriptionsByIri) {
    setDataProperty<ModelData>(model)(model.modelData, 'descriptionsByIri', {
      // only add new, don't overwrite
      ...projectModel.descriptionsByIri,
      ...model.modelData.descriptionsByIri,
    });
  }

  if (projectModel.labelsByIri) {
    setDataProperty<ModelData>(model)(
      model.modelData,
      'labelsByIri',
      mergeLabelsByIri(model.modelData.labelsByIri, projectModel.labelsByIri)
    );
  }
};

const addNodesToDiagramModel = (projectModel: ProjectModel, model: Model) => {
  const modelName = projectModel.name;
  const models = [modelName];

  Object.values(projectModel.classes || {}).forEach(({ iri }) => {
    const node = createModelConceptClass({
      value: iri,
      models,
    });
    createOrUpdateData(
      node,
      model.nodeDataArray as ModelConceptClass[],
      modelName,
      model,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      model.addNodeData
    );

    // assign a color to the class if it is not already defined
    if (!model.modelData.colorIdsByIri[iri]) {
      setColorIdForIri(iri)(model);
    }
  });
};

const addPropertiesToDiagramModel = (
  projectModel: ProjectModel,
  model: Model,
  diagram: Diagram
) => {
  const modelName = projectModel.name;
  const models = [modelName];

  Object.values(projectModel.classes || {}).forEach(({ iri, parents }) => {
    parents.forEach((parent) => {
      if (!doesModelHaveClass(model, parent)) {
        return;
      }

      const link = createModelConceptHierarchy({
        fromIri: iri,
        toIri: parent,
        models,
      });
      createOrUpdateData(
        link,
        model.linkDataArray as ModelConceptHierarchy[],
        modelName,
        model,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        model.addLinkData
      );

      const linkLabel = createModelConceptHierarchyLabel({
        fromIri: iri,
        toIri: parent,
      });
      createLabelData(linkLabel, model);
    });
  });

  Object.values(projectModel.properties || {}).forEach(
    ({
      iri: value,
      domain: domains,
      range: ranges,
      isDatatype,
      propertyType,
    }) => {
      setPropertyType(value, propertyType)(model);

      if (isDatatype) {
        addModelsToAttributeModelData({ value, models })(model);

        ranges.forEach((range) => {
          addRangeToAttributeModelData({ value, range, models })(model);
        });

        domains.forEach((domain) => {
          if (!doesModelHaveClass(model, domain)) {
            return;
          }
          addDomainToAttributeModelData({ value, domain, models })(model);
        });
        return;
      }

      if (domains.length && ranges.length) {
        domains.forEach((fromIri) => {
          if (!doesModelHaveClass(model, fromIri)) {
            return;
          }

          ranges.forEach((toIri) => {
            if (!doesModelHaveClass(model, toIri)) {
              return;
            }

            const link = createModelConceptRelationship({
              fromIri,
              value,
              toIri,
              models,
            });
            createOrUpdateData(
              link,
              model.linkDataArray as ModelConceptRelationship[],
              modelName,
              model,
              // eslint-disable-next-line @typescript-eslint/unbound-method
              model.addLinkData
            );

            const linkLabel = createModelConceptRelationshipLabel({
              fromIri,
              value,
              toIri,
            });
            createLabelData(linkLabel, model);
          });
        });
      } else if (domains.length) {
        domains.forEach((fromIri) => {
          if (!doesModelHaveClass(model, fromIri)) {
            return;
          }
          addUnconnectedModelConceptRelationship({
            value,
            fromIri,
            models,
          })(model, diagram);
        });
      } else if (ranges.length) {
        ranges.forEach((toIri) => {
          if (!doesModelHaveClass(model, toIri)) {
            return;
          }
          addUnconnectedModelConceptRelationship({
            value,
            toIri,
            models,
          })(model, diagram);
        });
      } else {
        addUnconnectedModelConceptRelationship({
          value,
          models,
        })(model, diagram);
      }
    }
  );
};

const addRulesToDiagramModel = (
  projectModel: ProjectModel,
  model: Model,
  diagram: Diagram
) => {
  const existingRules = diagram.model.modelData.rulesById;
  const ruleModel = getRuleModel(diagram.model);

  Object.values(projectModel.rules || {}).forEach((rule) => {
    const { name, id, ifClause, thenClause } = rule;
    const isValidRule = isRuleValid(rule, ruleModel);
    // don't add invalid rules
    if (!isValidRule) {
      return;
    }
    // existing rule with the same id is only updated if it changed
    if (existingRules[id]) {
      if (!isEqual(rule, existingRules[id])) {
        updateRule(rule)(model);
      }
      return;
    }
    const rulesWithMatchingName = Object.values(existingRules).filter(
      (rule) => rule.name === name
    );

    if (!rulesWithMatchingName.length) {
      addRule(rule)(model);
    } else {
      // check if the rule is the same as an existing rule
      const areRulesEqual = rulesWithMatchingName.some(
        (existingRule) =>
          isEqual(ifClause, existingRule.ifClause) &&
          isEqual(thenClause, existingRule.thenClause)
      );
      if (!areRulesEqual) {
        // check for existing rule with name + _N
        const rulesWithMatchingNameAndSuffix = Object.values(
          existingRules
        ).filter((rule) => rule.name.startsWith(`${name}_`));

        const suffixes = rulesWithMatchingNameAndSuffix.map((rule) => {
          // regex to get the number after the last _
          const lastNumMatch = rule.name.match(/(.*)_([0-9]+)$/);
          return lastNumMatch?.[2] ? parseInt(lastNumMatch[2]) : 0;
        });
        const highestSuffix = Math.max(...suffixes);
        addRule({ ...rule, name: `${name}_${highestSuffix + 1}` })(model);
      }
    }
  });
};

export const addProjectModelsToDiagramModel =
  (projectModels: ProjectModel[]) => (model: Model, diagram: Diagram) => {
    // first add metadata and classes
    projectModels.forEach((projectModel) => {
      addMetadataToDiagramModel(projectModel, model);
      addNodesToDiagramModel(projectModel, model);
    });

    // add links and attributes second as they depend on the existence of classes
    projectModels.forEach((projectModel) =>
      addPropertiesToDiagramModel(projectModel, model, diagram)
    );

    // add rules last as they depend on the existence of links and attributes
    projectModels.forEach((projectModel) =>
      addRulesToDiagramModel(projectModel, model, diagram)
    );
  };
